import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Box,
  Button,
  Container,
  Dialog,
  Stack,
  SvgIcon,
  Typography,
  styled,
  useTheme
} from "@mui/material";
import {
  Bin20Px,
  BookmarkFill20Px,
  CheckmarkCircle24Px,
  Warning24Px,
  ErrorPanel,
  ScanningIndicator,
  useScanIndicator,
  useToast,
  LeafIcon,
  Temperature24Px
} from "@qubit/autoparts";

import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { ProductCard } from "~/components/productCard/ProductCardRootWithSideRibbon";
import useFlag from "~/config/flags";
import MultiPort from "~/features/autostoreBin/MultiPort";
import { SelectTempZoneModal } from "~/features/autostoreMain/SelectTempZoneModal";
import { useCantInductReasons } from "~/hooks/useCantInductReasons";
import { useGetMultiTempGridInfo } from "~/hooks/useGetMultiTempGridInfo";
import { useInactivityResetTimer } from "~/hooks/useInactivityResetTimer";
import { useIsInventoryDateValid } from "~/hooks/useIsInventoryDateValid";
import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import usePortStatus from "~/hooks/usePortStatus";
import { useShouldListenToGridEvents } from "~/hooks/useShouldListenToGridEvents";

import { useBarcodeScanner, useKeyDownHandler } from "~/lib/barCodeScan";
import {
  checkIsExpiration,
  getBarcodeValue,
  searchProduct
} from "~/lib/helpers";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { useGridV2Subscription } from "~/lib/signalr";
import usePromiseInterval from "~/lib/usePromiseIntervalEffect";

import {
  nextEmptyBin,
  fetchPortStatus,
  resetPortBinData
} from "~/redux/actions";
import { setCurrentEmptyBin } from "~/redux/actions/autostore";
import { clearSelectedVariant } from "~/redux/actions/inventory";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectUsersClientId } from "~/redux/selectors/authSelectors";
import {
  selectIsInventoryAtPort,
  selectSelectedRow
} from "~/redux/selectors/autostorePutawaySelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import {
  selectIsShortSideBin,
  selectThisWorkstation
} from "~/redux/selectors/workstationsSelectors";

import { warehouseApi } from "~/redux/warehouse/warehouseApi";
import {
  useCloseWorkstationMutation,
  usePostWorkstationInductionConfigurationMutation
} from "~/redux/warehouse/workstation.hooks";
import {
  AutostoreEvent,
  NextEmptyBinResponse,
  PutAwayTaskSummaryDto,
  TemperatureZone
} from "~/types/api";

import { AutostorePutawaySearch } from "./AutostorePutawaySearch";
import { Banners } from "./Banners";
import { BinQuantityWidget } from "./BinQuantityWidget";
import CantInductAlert from "./CantInductAlert";
import { ClearBinOverridesButton } from "./ClearBinOverridesButton";
import { ConfirmInductionButton } from "./ConfirmInductionButton";
import { ExitPreviousBinButton } from "./ExitPreviousBinButton";
import { FoodSafetyConfigurationModal } from "./FoodSafetyConfigurationModal";
import { GetNewBinButton } from "./GetNewBinButton";
import GetPreviousBinButton from "./GetPreviousBinButton";
import InventoryDateField from "./InventoryDateField";
import LotNumberField from "./LotNumberField";
import { PutawayTasksTable } from "./PutawayTasksTable";
import { QuantityField } from "./QuantityField";
import {
  selectRow,
  setChangedQuantity,
  setInventoryDate,
  setSearchData,
  setIsPutawayTaskTableRefreshed,
  setPortPollingActive,
  setClosePortBtnDisabled,
  selectNewCompartment,
  reset,
  setOnLeaveInputTextShown,
  setLotNumber,
  selectIsLotNumberFieldFocused,
  selectCompartment,
  selectShouldShowGetNewBinButton,
  setHasUserChangedBin,
  setShouldShowGetNewBinButton,
  openFoodSafetyModal,
  selectIsPreviousBinFlow,
  selectPreviousPutawayTask,
  setPreviousBinNumber,
  clearBinOverrides,
  openSanitizeModal,
  clearSanitizedBins
} from "./autostorePutaway.slice";
import { AdjustQuantityModal } from "./modals/AdjustQuantityModal";
import BinNotEmptyCompartment from "./modals/BinNotEmptyCompartment";
import { BinNotEmptyModal } from "./modals/BinNotEmptyModal";
import FlagBinModal from "./modals/FlagBinModal";
import InventoryHoldModal from "./modals/InventoryHoldModal";
import { SanitizeModal } from "./modals/SanitizeModal";
import { useCubing } from "./useCubing";
import { useGetPutawayTasks } from "./useGetPutawayTasks";
import { useStartInduction } from "./useStartInduction";

const ProductFieldsContainer = styled(Box)`
  margin-top: 18px;
  display: grid;
  grid-template-columns: minmax(0, 210px) minmax(190px, 1fr);
  gap: 16px;
  align-items: center;
`;

type Props = {
  viewTitle: ViewNameTranslation;
};

function FoodSafetyHelpIcon() {
  const { palette } = useTheme();
  return <Warning24Px fill={palette.productCardSafety.warning} />;
}

function SanitizeHelpIcon() {
  const { palette } = useTheme();
  return <LeafIcon sx={{ color: palette.productCardSafety.sanitize }} />;
}

export const AutostorePutawayV2 = (props: Props) => {
  const { viewTitle } = props;

  const nextEmptyBinByPort = useAppSelector(
    (state) => state.autostore.nextEmptyBinByPort
  );
  const portStateByPort = useAppSelector(
    (state) => state.autostore.portStateByPort
  );
  const siteAllPortIds = useAppSelector(
    (state) => state.workstations.siteAllPortIds
  );
  const currentSelectedBin = useAppSelector(
    (state) => state.autostore.currentEmptyBin
  );
  const foodSafetyModalOpen = useAppSelector(
    (state) => state.autostorePutaway.isFoodSafetyModalOpen
  );
  const isSanitizeModalOpen = useAppSelector(
    (state) => state.autostorePutaway.isSanitizeModalOpen
  );
  const portOverrides = useAppSelector(
    (state) => state.autostorePutaway.wholeBinPortOverrides
  );
  const hasSelectedAlreadyWholeBins = useAppSelector(
    (state) => state.autostorePutaway.hasSelectedAlreadyWholeBins
  );
  const isUsingTempConfig = !!portOverrides.length;

  const dispatch = useAppDispatch();
  const { errorToast, successToast } = useToast();
  const { palette } = useTheme();

  const currentSelectedPortId = currentSelectedBin?.openBinResponse.portId;
  const currentSelectedBinId = currentSelectedBin?.openBinResponse.binId;
  const selectedRowId = useAppSelector(
    (state) => state.autostorePutaway.selectedRowId
  );
  const selectedCompartment = useAppSelector(
    (state) => state.autostorePutaway.selectedCompartment
  );
  const newSelectedCompartment = useAppSelector(
    (state) => state.autostorePutaway.newSelectedCompartment
  );
  const inventoryDate = useAppSelector(
    (state) => state.autostorePutaway.inventoryDate
  );
  const searchData = useAppSelector(
    (state) => state.autostorePutaway.searchData
  );
  const isPutawayTaskTableRefreshed = useAppSelector(
    (state) => state.autostorePutaway.isPutawayTaskTableRefreshed
  );
  const portPollingActive = useAppSelector(
    (state) => state.autostorePutaway.portPollingActive
  );
  const sanitizedBins = useAppSelector(
    (state) => state.autostorePutaway.sanitizedBins
  );
  const isLotNumberFieldFocused = useAppSelector(selectIsLotNumberFieldFocused);
  const shouldShowGetNewBinButton = useAppSelector(
    selectShouldShowGetNewBinButton
  );
  const isPreviousBinFlow = useAppSelector(selectIsPreviousBinFlow);
  const previousPutawayTask = useAppSelector(selectPreviousPutawayTask);
  const isShortSideBin = useAppSelector(selectIsShortSideBin);
  const pageLimit = 7;
  const {
    putawayTasks,
    putawayTasksLoading,
    putawayTasksFetching,
    originalArgs: putawayTaskSearchArgs,
    error: getPutawayTasksError
  } = useGetPutawayTasks(pageLimit, searchData);
  const selectedRow = useAppSelector((state) =>
    selectSelectedRow(state, putawayTasks)
  );

  const { cubingData, selectedBinCubingData } = useCubing({
    selectedRow
  });

  const isInventoryDateValid = useIsInventoryDateValid(putawayTasks);
  const taskToDisplay = isPreviousBinFlow ? previousPutawayTask : selectedRow;

  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const clientId = useAppSelector(selectUsersClientId);
  const fulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const binFlaggingEnabled = fulfillmentCenter?.binFlaggingEnabled;
  const { inv_inventoryDateLabel, putaway_multipleSearchTerms } =
    useAppSelector(selectServerConfig);
  // Hooks
  const { t } = useTranslation();
  const trackedPageName = useMixpanelPageName();
  const shouldListenToGridEvents = useShouldListenToGridEvents();
  useKeyDownHandler();
  const { areAllPortsReady, areSomePortsReady } = usePortStatus(
    portStateByPort,
    siteAllPortIds,
    currentSelectedPortId
  );
  const { cantInductReasonsMap, isPutawayTaskIneligible } =
    useCantInductReasons(taskToDisplay);
  const [scanState, setScanState] = useScanIndicator();
  const {
    inductionFoodSafetyWorkflow: enableFoodSafetyFlow,
    tablesV2Enhanced,
    enableColorChanges
  } = useFlag();
  const tertiaryActionSx = useMemo(
    () =>
      enableColorChanges
        ? {
            "&&": {
              backgroundColor:
                palette.autostoreDesignSystem.surface.static.default,
              color: palette.autostoreDesignSystem.text.onStandard.primary,
              border: `1px solid ${palette.autostoreDesignSystem.border.onStandard.surfaceDefault}`,
              "@media (hover: hover)": {
                "&:hover": {
                  backgroundColor:
                    palette.autostoreDesignSystem.surface.interactive.base.hover
                }
              },
              "&.Mui-disabled": {
                backgroundColor:
                  palette.autostoreDesignSystem.surface.interactive.base
                    .disabled,
                color: palette.autostoreDesignSystem.text.onStandard.disabled,
                borderColor:
                  palette.autostoreDesignSystem.border.onStandard.inputDisabled
              }
            }
          }
        : {},
    [enableColorChanges, palette]
  );
  const bottomActionHeightSx = enableColorChanges
    ? { height: 75, boxSizing: "border-box" as const }
    : {};
  const mutedActionSx = enableColorChanges
    ? {
        ...bottomActionHeightSx,
        "&&": {
          backgroundColor:
            palette.autostoreDesignSystem.surface.interactive.muted.default,
          color: palette.autostoreDesignSystem.text.onStandard.primary,
          border: "1px solid transparent",
          "@media (hover: hover)": {
            "&:hover": {
              backgroundColor:
                palette.autostoreDesignSystem.surface.interactive.muted.hover
            }
          },
          "&.Mui-disabled": {
            backgroundColor:
              palette.autostoreDesignSystem.surface.interactive.muted.default,
            color: palette.autostoreDesignSystem.text.onStandard.disabled
          }
        }
      }
    : {};
  const hasPutawayTableV2EnhancedFlag =
    tablesV2Enhanced.includes("inductionTableV2");
  const { isMultiTempGrid } = useGetMultiTempGridInfo();

  const hasNextEmptyBinCallCompleted =
    Object.keys(nextEmptyBinByPort).length >= 1;
  const isPlaceHoldEnabled =
    useAppSelector(selectIsInventoryAtPort) &&
    hasNextEmptyBinCallCompleted &&
    siteWorkstation;
  const areSomeBinsNotWhole =
    hasNextEmptyBinCallCompleted &&
    Object.values(nextEmptyBinByPort).some(
      (bin) => bin?.autostoreBinConfiguration?.numberOfCompartments !== 1
    );
  const areAllBinsWhole =
    hasNextEmptyBinCallCompleted &&
    Object.values(nextEmptyBinByPort).every(
      (bin) => bin?.autostoreBinConfiguration?.numberOfCompartments === 1
    );

  // Local State - Modals
  const [
    isBinNotEmptyCompartmentPanelOpen,
    setIsBinNotEmptyCompartmentPanelOpen
  ] = useState(false);
  const [isBinNotEmptyPanelOpen, setIsBinNotEmptyPanelOpen] = useState(false);
  const [isInventoryHoldModalOpen, setIsInventoryHoldModalOpen] =
    useState(false);
  const [isFlagBinModalOpen, setIsFlagBinModalOpen] = useState(false);
  const [isUnderReceivedModalOpen, setIsUnderReceivedModalOpen] =
    useState(false);
  const [isOverReceivedModalOpen, setIsOverReceivedModalOpen] = useState(false);
  const [isChangeTempZoneModalOpen, setIsChangeTempZoneModalOpen] =
    useState(false);

  // Local State
  const [newSelectedBin, setNewSelectedBin] =
    useState<NextEmptyBinResponse | null>(null);
  const [binAtPortSeconds, setBinAtPortSeconds] = useState(0);
  const [lastScannedBarcode, setLastScannedBarcode] = useState<string | null>(
    null
  );

  const shouldShowActionButtons =
    putawayTasksLoading || putawayTasks.length >= 0;

  const onInactivityModalClose = useCallback(() => {
    setIsBinNotEmptyCompartmentPanelOpen(false);
    dispatch(reset());
    dispatch(setClosePortBtnDisabled(true));
    dispatch(setPortPollingActive(false));
    if (shouldListenToGridEvents) {
      setBinAtPortSeconds(0);
    }
  }, [dispatch, shouldListenToGridEvents]);

  const { restartInactivityTimer } = useInactivityResetTimer({
    onInactivityModalClose
  });

  const handleBarcodeScan = (barcode: string) => {
    // if client expects to scan PO and product for one search (search within a search)
    if (putaway_multipleSearchTerms) {
      // if there are already 2 values in the search or the current search has no results, clear and set to new barcode value
      const newBarcodeArray =
        searchData.scannedBarcodes.length > 1 || putawayTasks.length === 0
          ? [barcode]
          : [...searchData.scannedBarcodes, barcode];
      dispatch(
        setSearchData({
          scannedBarcodes: newBarcodeArray,
          offset: 1
        })
      );
    } else {
      dispatch(
        setSearchData({
          scannedBarcodes: [barcode],
          offset: 1
        })
      );
    }
  };

  useBarcodeScanner<boolean>({
    findScanMatch: (buffer: string) => {
      setLastScannedBarcode(buffer);
      const barcodeValue = getBarcodeValue(buffer);
      handleBarcodeScan(barcodeValue);
      setScanState("success");
      restartInactivityTimer();
      mixpanelTrack({
        trackedPageName,
        type: "Scan",
        label: "Select Product",
        eventProperties: {
          Barcode: barcodeValue
        }
      });
      return true;
    },
    disabled: isBinNotEmptyPanelOpen || isLotNumberFieldFocused
  });

  const putawayTasksErrorMessage = getPutawayTasksError
    ? getMessageFromRtkError(getPutawayTasksError)
    : null;

  useEffect(() => {
    if (putawayTasksErrorMessage) {
      errorToast(putawayTasksErrorMessage);
    }
  }, [putawayTasksErrorMessage, errorToast]);

  // Clear port and bin state on first render
  useEffect(() => {
    dispatch(resetPortBinData());
    dispatch(
      setSearchData({
        scannedBarcodes: [],
        offset: 1
      })
    );
    dispatch(clearBinOverrides());
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-initialize-state
    setLastScannedBarcode(null);
    dispatch(selectRow(null));
    dispatch(setIsPutawayTaskTableRefreshed(false));
    dispatch(setPreviousBinNumber(null));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // clear bin overrides on unmount
  useEffect(
    () => () => {
      dispatch(clearBinOverrides());
      dispatch(clearSanitizedBins());
    },
    [dispatch]
  );

  const { isFoodSafetyItem, isSanitizeTargetBin } = selectedRow?.product ?? {};

  useEffect(() => {
    if (
      ((enableFoodSafetyFlow && isFoodSafetyItem) || isSanitizeTargetBin) &&
      !isUsingTempConfig &&
      // User confirmed existing whole bins only — don't reopen until overrides are cleared
      !hasSelectedAlreadyWholeBins &&
      // If all bins are already whole, no need to reconfigure any to whole bin
      areSomeBinsNotWhole
    ) {
      dispatch(openFoodSafetyModal());
    }
  }, [
    enableFoodSafetyFlow,
    isFoodSafetyItem,
    isSanitizeTargetBin,
    isUsingTempConfig,
    hasSelectedAlreadyWholeBins,
    dispatch,
    areSomeBinsNotWhole,
    // Re-open after cancel when the user selects a (possibly different) food safety row
    selectedRowId
  ]);

  // After scanning barcode, if there are no putaway tasks returned, check backend products.
  // If product doesn't exist display an error.
  useEffect(() => {
    if (
      putawayTasks.length === 0 &&
      clientId &&
      !putawayTasksFetching &&
      putawayTaskSearchArgs?.searches?.length
    ) {
      const searches = putawayTaskSearchArgs.searches;
      const productSearchFunc = async () => {
        const hits = await searchProduct(searches);
        if (!hits.length) {
          errorToast(t("scanned product does not exist"));
        }
      };
      void productSearchFunc();
    }
  }, [
    putawayTasks.length,
    clientId,
    errorToast,
    t,
    putawayTaskSearchArgs,
    putawayTasksFetching
  ]);

  const handleFetchPortStatus = async (portId?: number) => {
    await dispatch(fetchPortStatus({ portId }));
  };

  const { isStartInductionError, restartInduction } = useStartInduction();
  const [
    postInductionConfiguration,
    { isLoading: isSavingInductionConfiguration }
  ] = usePostWorkstationInductionConfigurationMutation();
  const [closeWorkstation, { isLoading: isClosingWorkstation }] =
    useCloseWorkstationMutation();

  // Port polling: if log publisher enabled, poll port and log publisher state
  const waitInterval = 7;
  usePromiseInterval(
    async () => {
      setBinAtPortSeconds((binAtPortSecondsState) => binAtPortSecondsState + 1);
      if (binAtPortSeconds > waitInterval && siteWorkstation) {
        await Promise.all(
          siteWorkstation.ports.map((port) => {
            if (!portStateByPort[port.portId]?.getPortResponse.isReady)
              return handleFetchPortStatus(port.portId);

            return Promise.resolve();
          })
        );
      }
    },
    1000,
    !areAllPortsReady && shouldListenToGridEvents && !isStartInductionError
  );

  // If port polling active but log publisher not active for grid, poll port every half second
  usePromiseInterval(
    async () => {
      await Promise.all(
        (siteWorkstation?.ports ?? []).map((port) =>
          handleFetchPortStatus(port.portId)
        )
      );
    },
    500,
    portPollingActive && !shouldListenToGridEvents && !isStartInductionError
  );

  // Reset binAtPortSeconds, when bin arrives at port
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (areAllPortsReady && binAtPortSeconds > 0) {
      setBinAtPortSeconds(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areAllPortsReady]);

  // Stop polling when bin arrives
  useEffect(() => {
    if (!shouldListenToGridEvents && areAllPortsReady) {
      dispatch(setPortPollingActive(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portStateByPort]);

  const abortRef = useRef(() => {});
  useEffect(() => () => abortRef.current(), []);

  const onGetNewBinSuccess = () => {
    dispatch(setShouldShowGetNewBinButton(false));
    dispatch(warehouseApi.util.invalidateTags(["cubing data"]));
    successToast(t("new bin requested"));
  };

  const onBinNotEmptySuccess = async () => {
    await dispatch(
      nextEmptyBin({ portId: newSelectedBin?.openBinResponse?.portId })
    );
    await dispatch(
      fetchPortStatus({ portId: newSelectedBin?.openBinResponse?.portId })
    );
    dispatch(warehouseApi.util.invalidateTags(["cubing data"]));
    dispatch(selectNewCompartment(null));
    setNewSelectedBin(null);
  };

  // If there's only one putaway default to that task
  useEffect(() => {
    if (putawayTasks.length === 1) {
      dispatch(selectRow(putawayTasks[0].putAwayTaskId));
    } else {
      dispatch(selectRow(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [putawayTasks]);

  // Set lotNumber and inventory date based on selected row
  useEffect(() => {
    dispatch(setLotNumber(taskToDisplay?.lotNumber || ""));

    const inventoryDate = checkIsExpiration(inv_inventoryDateLabel)
      ? taskToDisplay?.expirationDate
      : taskToDisplay?.manufactureDate;
    dispatch(setInventoryDate(inventoryDate || null));
  }, [taskToDisplay, inv_inventoryDateLabel, dispatch]);

  // Enable/disable showing text in search bar
  useEffect(() => {
    dispatch(setOnLeaveInputTextShown(!isPutawayTaskTableRefreshed));
  }, [dispatch, isPutawayTaskTableRefreshed]);

  // Clear selected row
  useEffect(() => {
    if (
      (putawayTasks.length === 0 && !putawayTasksLoading) ||
      (getPutawayTasksError && searchData.scannedBarcodes.length > 0)
    ) {
      dispatch(selectRow(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [putawayTasks.length, getPutawayTasksError]);

  const gridSub = (data: AutostoreEvent) => {
    if (data.case !== "BinModeChange" || data.event.binMode !== "O") {
      return;
    }

    if (
      data.event.gridId === siteWorkstation?.autostoreGridId &&
      !!data.event.portId &&
      siteAllPortIds.includes(data.event.portId)
    ) {
      // if "binOpened" event is received, trigger port polling
      setBinAtPortSeconds((time) => time + waitInterval);
    }
  };
  useGridV2Subscription(gridSub);

  const handleClickBinNotEmptyButton = () => {
    if (currentSelectedBin) {
      Sentry.captureMessage(
        `Bin not empty was clicked on bin number ${currentSelectedBin.openBinResponse.binId}`,
        "info"
      );
      if (shouldListenToGridEvents) {
        setBinAtPortSeconds(0);
      }
    }
    mixpanelTrack({
      trackedPageName,
      type: "Button Click",
      label: "Bin Not Empty",
      eventProperties: {
        Product: taskToDisplay?.product,
        BinId: currentSelectedBin?.openBinResponse.binId
      }
    });

    // Skip compartment selection when there's only one port/bin and it's whole
    const isSinglePort = siteWorkstation?.ports.length === 1;
    const isSingleWholeBin = isSinglePort && areAllBinsWhole;

    if (isSingleWholeBin) {
      setNewSelectedBin(nextEmptyBinByPort[siteAllPortIds[0]]);
      dispatch(selectNewCompartment(0));
      setIsBinNotEmptyPanelOpen(true);
    } else {
      setIsBinNotEmptyCompartmentPanelOpen(true);
    }
  };

  // Handle setting initial quantity value
  useEffect(() => {
    if (isPreviousBinFlow) {
      return;
    }

    if (!selectedRow?.decantingRate) {
      dispatch(setChangedQuantity(undefined));
    } else {
      const decantRate = selectedRow?.decantingRate?.value || Number.MAX_VALUE;
      const remaining = selectedRow?.remaining.value || 1;
      dispatch(setChangedQuantity(Math.min(decantRate, remaining)));
    }
  }, [selectedRowId, isPreviousBinFlow, dispatch, selectedRow]);

  const handleClickPlaceInventoryHold = () => {
    mixpanelTrack({
      trackedPageName,
      type: "Button Click",
      label: "Inventory Hold",
      eventProperties: {
        Product: taskToDisplay?.product
      }
    });
    setIsInventoryHoldModalOpen((prev) => !prev);
  };

  const handleClickFlagBin = () => {
    if (isFlagBinModalOpen) {
      return;
    }

    mixpanelTrack({
      trackedPageName,
      type: "Button Click",
      label: "Flag Bin",
      eventProperties: {
        Product: taskToDisplay?.product
      }
    });
    setIsFlagBinModalOpen(true);
  };

  const handleChangeTempZone = async (selectedZone: TemperatureZone) => {
    if (!siteWorkstation) {
      errorToast(t("no workstation selected"));
      return;
    }

    try {
      await postInductionConfiguration({
        autostoreGridId: siteWorkstation.autostoreGridId,
        workstationId: siteWorkstation.id,
        body: { temperatureZone: selectedZone }
      }).unwrap();
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
      return;
    }

    // Close workstation before restarting induction to ensure we get new bins.
    try {
      await closeWorkstation({
        autostoreGridId: siteWorkstation.autostoreGridId,
        workstationId: siteWorkstation.id
      }).unwrap();
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
      return;
    }

    setIsChangeTempZoneModalOpen(false);
    // Get new temp zone products in table
    dispatch(selectRow(null));
    dispatch(resetPortBinData());

    await restartInduction();
  };

  const onConfirmChangeSuggestedBin = useCallback(
    (newBin: NextEmptyBinResponse) => {
      dispatch(setHasUserChangedBin(true));
      dispatch(setCurrentEmptyBin(newBin));

      const compartment = cubingData?.find(
        (bin) => bin.binNumber === newBin.openBinResponse.binId
      )?.suggestedCompartment;
      if (compartment) {
        dispatch(selectCompartment(compartment - 1));
      }
    },
    [dispatch, cubingData]
  );

  const handleAdjustQuantityModalSuccess = () => {
    setIsUnderReceivedModalOpen(false);
    setIsOverReceivedModalOpen(false);
    dispatch(selectRow(null));
  };

  const handleSelectRow = (row: PutAwayTaskSummaryDto) => {
    if (row.putAwayTaskId !== selectedRowId && !isPreviousBinFlow) {
      dispatch(selectRow(row.putAwayTaskId));
      mixpanelTrack({
        trackedPageName,
        type: "Button Click",
        label: "Select Product",
        eventProperties: {
          Product: row.product
        }
      });
    }
  };

  const onUnderReceivedClick = () => {
    mixpanelTrack({
      trackedPageName,
      type: "Button Click",
      label: "Under Received",
      eventProperties: {
        Product: taskToDisplay?.product
      }
    });
    setIsUnderReceivedModalOpen(true);
  };

  const onOverReceivedClick = () => {
    mixpanelTrack({
      trackedPageName,
      type: "Button Click",
      label: "Over Received",
      eventProperties: {
        Product: taskToDisplay?.product
      }
    });
    setIsOverReceivedModalOpen(true);
  };

  const renderBinButton = useCallback(
    (bin: NextEmptyBinResponse) => {
      const showButton =
        !foodSafetyModalOpen &&
        isSanitizeTargetBin &&
        !isPreviousBinFlow &&
        bin.autostoreBinConfiguration?.configurationType === 1 &&
        !sanitizedBins.includes(bin.openBinResponse.binId);

      if (!showButton) {
        return null;
      }

      return (
        <Button
          variant={enableColorChanges ? "subtle" : "contained"}
          onClick={() => {
            mixpanelTrack({
              trackedPageName,
              type: "Button Click",
              label: "Sanitize",
              eventProperties: {
                BinId: bin.openBinResponse.binId
              }
            });
            onConfirmChangeSuggestedBin(bin);
            dispatch(openSanitizeModal());
          }}
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            top: 0,
            width: "200px",
            maxWidth: "100%",
            alignSelf: "center",
            justifySelf: "center",
            ...tertiaryActionSx
          }}
        >
          {t("sanitize")}
        </Button>
      );
    },
    [
      dispatch,
      isSanitizeTargetBin,
      isPreviousBinFlow,
      sanitizedBins,
      t,
      onConfirmChangeSuggestedBin,
      foodSafetyModalOpen,
      trackedPageName,
      tertiaryActionSx,
      enableColorChanges
    ]
  );

  const renderSanitizedfIndicator = (binId: number) => {
    if (sanitizedBins.includes(binId)) {
      return (
        <Stack direction="row" mt={1} spacing={1} justifyContent="center">
          <CheckmarkCircle24Px fill={palette.success.dark} />
          <Typography>{t("sanitized")}</Typography>
        </Stack>
      );
    }
    return null;
  };

  const AutostoreBinView = (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        gridColumn: "span 2"
      }}
    >
      <BinQuantityWidget />
      <Box
        sx={{
          maxWidth: isShortSideBin ? "900px" : "1100px",
          width: "100%",
          margin: "0 auto"
        }}
      >
        <MultiPort
          pickQuantity={0}
          nextEmptyBinByPort={nextEmptyBinByPort}
          workstation={siteWorkstation}
          showBinIdWhenWaiting={false}
          currentSelectedBinId={
            selectedRowId ? currentSelectedBinId : undefined
          }
          selectedCompartment={selectedCompartment}
          renderBinButton={renderBinButton}
          renderBelowBinElement={renderSanitizedfIndicator}
          onBinClick={(_compartment, binNumber) => {
            if (!areSomePortsReady || !selectedRowId || isPreviousBinFlow)
              return;
            const bin = Object.values(nextEmptyBinByPort).find(
              (bin) => bin?.openBinResponse.binId === binNumber
            );
            if (bin) {
              onConfirmChangeSuggestedBin(bin);
            }
          }}
          requiresWholeBin={isFoodSafetyItem || isSanitizeTargetBin}
          isShortSideBin={isShortSideBin}
          trackedPageName={trackedPageName}
        />
      </Box>

      {/* Bin related buttons (top-right of page) */}
      {shouldShowActionButtons && (
        <Stack
          justifyContent={isMultiTempGrid ? "space-between" : "space-around"}
          pt={4}
          mb={5}
        >
          <Button
            variant="subtle"
            size="large"
            onClick={handleClickBinNotEmptyButton}
            disabled={
              !areSomePortsReady ||
              isBinNotEmptyCompartmentPanelOpen ||
              isPreviousBinFlow
            }
            sx={{ minWidth: "245px", height: "52px", ...tertiaryActionSx }}
            startIcon={<SvgIcon viewBox="0 0 20 20" component={Bin20Px} />}
          >
            {t("bin not empty")}
          </Button>
          {binFlaggingEnabled && (
            <Button
              variant="subtle"
              size="large"
              onClick={handleClickFlagBin}
              disabled={!areSomePortsReady}
              sx={{ minWidth: "245px", height: "52px", ...tertiaryActionSx }}
              startIcon={
                <SvgIcon viewBox="0 0 20 20" component={BookmarkFill20Px} />
              }
            >
              {t("flag bin")}
            </Button>
          )}
          {isMultiTempGrid && (
            <Button
              variant="subtle"
              size="large"
              onClick={() => {
                mixpanelTrack({
                  trackedPageName,
                  type: "Button Click",
                  label: "Change Temp Zone"
                });
                setIsChangeTempZoneModalOpen(true);
              }}
              sx={{ minWidth: "245px", height: "52px", ...tertiaryActionSx }}
              startIcon={
                <SvgIcon viewBox="0 0 24 24" component={Temperature24Px} />
              }
            >
              {t("change temp zone")}
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );

  const searchAndFilterPutawayTable = useMemo(
    () => <AutostorePutawaySearch />,
    []
  );
  const { setMenuItems } = useNavbar({
    centerComponent: !hasPutawayTableV2EnhancedFlag
      ? searchAndFilterPutawayTable
      : undefined,
    viewTitle
  });

  useEffect(() => {
    setMenuItems([
      {
        textContent: t("refresh"),
        trackingLabel: "Refresh",
        actionCb: () => {
          dispatch(setIsPutawayTaskTableRefreshed(true));
          dispatch(
            setSearchData({
              scannedBarcodes: [],
              offset: 1
            })
          );
          dispatch(selectRow(null));
        }
      }
    ]);
  }, [dispatch, setMenuItems, t]);

  return (
    <Container
      maxWidth="xl"
      sx={{
        display: "grid",
        gridTemplateRows: "285px minmax(620px, 1fr) 75px",
        gridTemplateColumns: "1fr 0.40fr",
        height: "calc(100vh - 56px)",
        gap: 2,
        pb: 2
      }}
    >
      {siteWorkstation ? (
        AutostoreBinView
      ) : (
        <ErrorPanel
          sx={{ gridColumn: "span 2" }}
          message={t("no autostore port selected")}
          mx={3}
        />
      )}

      <Box sx={{ position: "relative" }}>
        <Stack
          direction="row"
          spacing={2}
          my={2}
          position="absolute"
          top="-70px"
        >
          <GetPreviousBinButton />
          {isUsingTempConfig && <ClearBinOverridesButton />}
        </Stack>
        <PutawayTasksTable
          inv_inventoryDateLabel={inv_inventoryDateLabel}
          pageLimit={pageLimit}
          selectedRow={selectedRow}
          handleSelectRow={handleSelectRow}
          hasPutawayTableV2EnhancedFlag={hasPutawayTableV2EnhancedFlag}
        />
      </Box>

      {taskToDisplay ? (
        <Box
          minWidth="0"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            ...(isPutawayTaskIneligible
              ? {
                  height: "100%",
                  minHeight: 0
                }
              : {})
          }}
        >
          <ProductCard.Root
            productName={undefined}
            imageFileName={taskToDisplay.product.imageFilename}
            tempZone={taskToDisplay.product.temperatureZone}
            warning={taskToDisplay.product.isFoodSafetyItem}
            sanitize={isSanitizeTargetBin}
            bottomSection={
              !isPutawayTaskIneligible ? (
                <ProductFieldsContainer>
                  <Stack direction="column" gap={2}>
                    <LotNumberField
                      isRequired={taskToDisplay?.product.isLotNumberRequired}
                    />
                    <InventoryDateField
                      inventoryDate={dayjs(inventoryDate)}
                      inv_inventoryDateLabel={inv_inventoryDateLabel}
                      isDateValid={isPreviousBinFlow || isInventoryDateValid}
                      setInventoryDate={(date) =>
                        dispatch(setInventoryDate(date?.toDate() || null))
                      }
                    />
                  </Stack>
                  <QuantityField selectedRow={taskToDisplay} />
                </ProductFieldsContainer>
              ) : undefined
            }
            disableGutters
            containerSx={{
              display: "contents"
            }}
            sx={{
              ...(isPutawayTaskIneligible
                ? {
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    width: "stretch"
                  }
                : {})
            }}
            ineligible={isPutawayTaskIneligible}
          >
            <ProductCard.TruncatedProductName
              productName={taskToDisplay.product.productName}
              sku={taskToDisplay.product.sku}
              upcs={[taskToDisplay.product.upc]}
              key={taskToDisplay.putAwayTaskId}
              warning={taskToDisplay.product.isFoodSafetyItem}
              sanitize={isSanitizeTargetBin}
            />
          </ProductCard.Root>

          <Stack flexShrink={isPutawayTaskIneligible ? 0 : undefined} gap={2}>
            <CantInductAlert cantInductReasonsMap={cantInductReasonsMap} />

            {taskToDisplay.product.isFoodSafetyItem && (
              <Stack direction="row" spacing={1}>
                <FoodSafetyHelpIcon />
                <Typography variant="body1">
                  {t("this is a food safety item")}
                </Typography>
              </Stack>
            )}

            {taskToDisplay.product.isSanitizeTargetBin && (
              <Stack direction="row" spacing={1}>
                <SanitizeHelpIcon />
                <Typography variant="body1">
                  {t("this item requires a sanitized bin")}
                </Typography>
              </Stack>
            )}

            <Banners maxFillQuantity={selectedBinCubingData?.maxFillQuantity} />
          </Stack>
        </Box>
      ) : (
        <ProductCard.Loading marginTop="0" height="579px" disableGutters />
      )}

      {enableColorChanges ? (
        <>
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            gap={3}
            sx={{ height: 75 }}
          >
            {shouldShowActionButtons && (
              <>
                <Button
                  variant="subtle"
                  size="large"
                  startIcon={<ErrorOutlineIcon />}
                  sx={{ ...tertiaryActionSx, ...bottomActionHeightSx }}
                  onClick={handleClickPlaceInventoryHold}
                  disabled={!isPlaceHoldEnabled}
                >
                  {t("inventory hold")}
                </Button>
                <Button
                  variant="subtle"
                  size="large"
                  onClick={onUnderReceivedClick}
                  disabled={
                    !selectedRow || !siteWorkstation || isPreviousBinFlow
                  }
                  sx={mutedActionSx}
                >
                  {t("under received")}
                </Button>
                <Button
                  variant="subtle"
                  size="large"
                  onClick={onOverReceivedClick}
                  disabled={
                    !selectedRow || !siteWorkstation || isPreviousBinFlow
                  }
                  sx={mutedActionSx}
                >
                  {t("over received")}
                </Button>
              </>
            )}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            gap={3}
            sx={{ height: 75 }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                height: 75,
                "& .MuiButton-root": {
                  height: 75,
                  width: "100%",
                  boxSizing: "border-box"
                }
              }}
            >
              {isPreviousBinFlow ? (
                <ExitPreviousBinButton />
              ) : shouldShowGetNewBinButton ? (
                <GetNewBinButton onSuccess={onGetNewBinSuccess} />
              ) : (
                <ConfirmInductionButton />
              )}
            </Box>
          </Stack>
        </>
      ) : (
        <>
          <Stack flexDirection="row" justifyContent="flex-end" gap={3}>
            {shouldShowActionButtons && (
              <>
                <Button
                  variant="subtle"
                  size="large"
                  onClick={onUnderReceivedClick}
                  disabled={
                    !selectedRow || !siteWorkstation || isPreviousBinFlow
                  }
                >
                  {t("under received")}
                </Button>
                <Button
                  variant="subtle"
                  size="large"
                  onClick={onOverReceivedClick}
                  disabled={
                    !selectedRow || !siteWorkstation || isPreviousBinFlow
                  }
                >
                  {t("over received")}
                </Button>
                <Button
                  variant="subtle"
                  size="large"
                  startIcon={<ErrorOutlineIcon />}
                  onClick={handleClickPlaceInventoryHold}
                  disabled={!isPlaceHoldEnabled}
                >
                  {t("inventory hold")}
                </Button>
              </>
            )}
          </Stack>
          {isPreviousBinFlow ? (
            <ExitPreviousBinButton />
          ) : shouldShowGetNewBinButton ? (
            <GetNewBinButton onSuccess={onGetNewBinSuccess} />
          ) : (
            <ConfirmInductionButton />
          )}
        </>
      )}

      {/* Modals */}
      <InventoryHoldModal
        isOpen={isInventoryHoldModalOpen}
        onClose={() => setIsInventoryHoldModalOpen(false)}
        nextEmptyBinByPort={nextEmptyBinByPort}
        currentSelectedBin={currentSelectedBin}
      />
      <FlagBinModal
        isOpen={isFlagBinModalOpen}
        onClose={() => setIsFlagBinModalOpen(false)}
        nextEmptyBinByPort={nextEmptyBinByPort}
        portStateByPort={portStateByPort}
        workstation={siteWorkstation}
        shouldCloseBinUponSubmit={!isPreviousBinFlow}
      />

      <Dialog
        onClose={() => {
          setIsBinNotEmptyPanelOpen(false);
          setNewSelectedBin(null);
          dispatch(selectNewCompartment(null));
          dispatch(clearSelectedVariant());
        }}
        open={isBinNotEmptyPanelOpen}
        maxWidth="tablet"
      >
        {newSelectedCompartment != null && (
          <BinNotEmptyModal
            portId={newSelectedBin?.openBinResponse.portId}
            binId={
              newSelectedBin?.autostoreBinCompartments.find(
                (comp) =>
                  comp.autostoreCompartmentNumber === newSelectedCompartment + 1
              )?.binId
            }
            setIsBinNotEmptyPanelOpen={setIsBinNotEmptyPanelOpen}
            onSuccess={onBinNotEmptySuccess}
          />
        )}
      </Dialog>
      <BinNotEmptyCompartment
        isOpen={isBinNotEmptyCompartmentPanelOpen}
        setIsBinNotEmptyCompartmentPanelOpen={
          setIsBinNotEmptyCompartmentPanelOpen
        }
        setIsBinNotEmptyPanelOpen={setIsBinNotEmptyPanelOpen}
        workstation={siteWorkstation}
        nextEmptyBinByPort={nextEmptyBinByPort}
        newSelectedBin={newSelectedBin}
        setNewSelectedBin={setNewSelectedBin}
        newSelectedCompartment={newSelectedCompartment}
        setNewSelectedCompartment={(compartment) =>
          dispatch(selectNewCompartment(compartment))
        }
      />
      <ScanningIndicator
        scanState={scanState}
        scannedBarcode={lastScannedBarcode}
        placeholderText="Scan Product"
      />
      {foodSafetyModalOpen && <FoodSafetyConfigurationModal />}
      <SelectTempZoneModal
        open={isChangeTempZoneModalOpen}
        onClose={() => setIsChangeTempZoneModalOpen(false)}
        onStart={handleChangeTempZone}
        isSubmitting={isSavingInductionConfiguration || isClosingWorkstation}
        variant="change"
      />
      {isSanitizeModalOpen && currentSelectedBinId && (
        <SanitizeModal binNumber={currentSelectedBinId} />
      )}
      <Dialog
        open={isUnderReceivedModalOpen}
        onClose={() => setIsUnderReceivedModalOpen(false)}
      >
        {isUnderReceivedModalOpen && selectedRow && (
          <AdjustQuantityModal
            putawayTask={selectedRow}
            closeModal={() => setIsUnderReceivedModalOpen(false)}
            handleSuccess={handleAdjustQuantityModalSuccess}
            variant="under-received"
          />
        )}
      </Dialog>
      <Dialog
        open={isOverReceivedModalOpen}
        onClose={() => setIsOverReceivedModalOpen(false)}
      >
        {isOverReceivedModalOpen && selectedRow && (
          <AdjustQuantityModal
            putawayTask={selectedRow}
            closeModal={() => setIsOverReceivedModalOpen(false)}
            handleSuccess={handleAdjustQuantityModalSuccess}
            variant="over-received"
          />
        )}
      </Dialog>
    </Container>
  );
};
